// The office control for the library rebuild (Session SCR1; the routes and the Guvnor tool rebuild_kb_library are Session BDX).
//
// After a restore from the weekly backup the manuals library has its page text but no page pictures, and its search passages are empty.
// Two bulk modes bring them back: PAGE PICTURES (drawn again from the originals in Google Drive) and SEARCH PASSAGES (cut again from the
// stored page text and embedded). This dialog is the office's way to start one without Guvnor, and it is exactly the Guvnor tool's path:
//
//   1. It PREVIEWS FIRST. "Check what it would do" calls the same route with preview on and shows the server's own plan and the number it
//      would act on (pages for pictures, documents for passages). Nothing starts from the first press.
//   2. A START QUOTES THE PREVIEW'S OWN NUMBER. The branded two-step confirm names that number and the server refuses a start whose number
//      no longer matches the plan, so what runs is what the person read. Changing the mode or the plan clears the preview.
//   3. ADMIN ONLY, as the routes are. The menu item is not offered to anyone else, and the server refuses them anyway.
//   4. A RUNNING REBUILD IS SHOWN, and can be stopped (a two-step confirm). What it has written stays; it finishes the page it is on.
//
// It lives behind an item in the overflow menu of the EXISTING manuals library tab (the calm screen rule: no new page or menu entry, one
// primary action per screen). It touches nothing in Drive and spends no vision budget.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ModalShell, MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "../../components/ModalShell";
import type { KnowledgeRebuildMode, KnowledgeRebuildPreview, KnowledgeRebuildStatus } from "../../lib/knowledgeTypes";

const MODE_WORDS: Record<KnowledgeRebuildMode, { title: string; blurb: string }> = {
  pictures: {
    title: "Page pictures",
    blurb: "Draw the missing page pictures again from the original files in Google Drive. No AI and no cost; it takes a while.",
  },
  passages: {
    title: "Search passages",
    blurb: "Cut the stored page text into search passages again and embed them, so the manuals search works. A small embedding cost.",
  },
};

const STATE_WORDS: Record<NonNullable<KnowledgeRebuildStatus["run"]>["state"], string> = {
  running: "Running",
  waiting_for_worker: "Waiting for the worker",
  interrupted: "Interrupted",
  stopped: "Stopped",
  done: "Finished",
  failed: "Failed",
};

const isLive = (s: KnowledgeRebuildStatus | null) => s?.run?.state === "running" || s?.run?.state === "waiting_for_worker";

function progressText(run: NonNullable<KnowledgeRebuildStatus["run"]>): string {
  const p = run.progress;
  if (run.mode === "pictures") return `${p.pagesRecovered.toLocaleString("en-GB")} of ${p.pagesTotal.toLocaleString("en-GB")} pages drawn, ${p.filesDone.toLocaleString("en-GB")} of ${p.filesTotal.toLocaleString("en-GB")} files`;
  return `${p.documentsDone.toLocaleString("en-GB")} of ${p.documentsTotal.toLocaleString("en-GB")} documents, ${p.passagesWritten.toLocaleString("en-GB")} passages written`;
}

export function KnowledgeRebuildDialog({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<KnowledgeRebuildMode>("pictures");
  const [status, setStatus] = useState<KnowledgeRebuildStatus | null>(null);
  const [preview, setPreview] = useState<KnowledgeRebuildPreview | null>(null);
  const [busy, setBusy] = useState<"preview" | "start" | "stop" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const loadStatus = useCallback(async (background: boolean) => {
    try {
      const res = await api.knowledge.drive.rebuild.status(background ? { background: true } : {});
      setStatus(res.rebuild);
    } catch (err) {
      if (!background) setError(friendlyApiError(err, "Could not read the rebuild status. Please try again."));
    }
  }, []);

  useEffect(() => {
    void loadStatus(false);
  }, [loadStatus]);

  // While a rebuild runs the progress refreshes by itself, as a BACKGROUND request (the session idle rule: a timer never keeps a session alive).
  const live = isLive(status);
  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => void loadStatus(true), 15000);
    return () => clearInterval(t);
  }, [live, loadStatus]);

  function chooseMode(next: KnowledgeRebuildMode) {
    setMode(next);
    setPreview(null);
    setError(null);
    setNote(null);
  }

  async function check() {
    setBusy("preview");
    setError(null);
    setNote(null);
    setPreview(null);
    try {
      setPreview(await api.knowledge.drive.rebuild.preview(mode));
    } catch (err) {
      setError(friendlyApiError(err, "The plan could not be worked out. Nothing was changed. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!preview) return;
    setBusy("start");
    setError(null);
    try {
      const res = await api.knowledge.drive.rebuild.start(preview.mode, preview.confirmNumber);
      setNote(res.message);
      setPreview(null);
      await loadStatus(false);
    } catch (err) {
      // A plan that moved since the preview is refused by the server with the new number: ask again rather than start on a stale one.
      setError(friendlyApiError(err, "The rebuild did not start. Nothing was changed. Check the plan again."));
      setPreview(null);
    } finally {
      setBusy(null);
    }
  }

  async function stop() {
    setBusy("stop");
    setError(null);
    try {
      const res = await api.knowledge.drive.rebuild.stop();
      setNote(res.message);
      await loadStatus(false);
    } catch (err) {
      setError(friendlyApiError(err, "The rebuild could not be asked to stop. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  const run = status?.run ?? null;

  return (
    <ModalShell ariaLabel="Rebuild the library after a restore" onClose={() => busy === null && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="flex max-h-[85vh] flex-col overflow-y-auto p-5">
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Rebuild the library after a restore</h2>
      <p className="mb-3 text-sm text-ele-body">
        After a restore from a backup the manuals keep their text but lose their page pictures and their search passages. Choose what to bring back,
        check the plan, then start it. Nothing in Drive is changed and nothing is deleted.
      </p>

      {status && (
        <p className="mb-3 text-sm text-ele-strong" data-testid="rebuild-missing">
          {status.pagesMissingPicture === 0
            ? "No page is missing its picture."
            : `${status.pagesMissingPicture.toLocaleString("en-GB")} page${status.pagesMissingPicture === 1 ? " is" : "s are"} missing a picture.`}
        </p>
      )}

      {run && (
        <div role="status" className="mb-3 rounded-ele border border-ele-hairline bg-ele-surface p-3 text-sm text-ele-body">
          <p className="text-ele-strong">
            Last rebuild ({MODE_WORDS[run.mode].title.toLowerCase()}): {STATE_WORDS[run.state]}
            {run.stopRequested && live ? ", stopping" : ""}
          </p>
          <p className="mt-1">{progressText(run)}</p>
          {run.progress.currentFile && live && <p className="mt-1 break-words">Working on {run.progress.currentFile}</p>}
          {run.filesFailed > 0 && <p className="mt-1">{run.filesFailed} file{run.filesFailed === 1 ? "" : "s"} could not be read; the rest carried on.</p>}
          {run.error && <p className="mt-1">{run.error}</p>}
          {live && (
            <div className="mt-2">
              <ConfirmButton
                onConfirm={() => void stop()}
                label="Stop the rebuild"
                confirmLabel="Press again to stop it"
                busy={busy === "stop"}
                busyLabel="Asking it to stop..."
                disabled={busy !== null || run.stopRequested}
                className={MODAL_SECONDARY_BTN}
              />
              <p className="mt-1 text-xs">What it has done stays. It finishes the page it is on.</p>
            </div>
          )}
        </div>
      )}

      {!live && (
        <>
          <fieldset className="mb-3">
            <legend className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body">What to bring back</legend>
            <div className="space-y-2">
              {(Object.keys(MODE_WORDS) as KnowledgeRebuildMode[]).map((m) => (
                <label key={m} className="flex cursor-pointer items-start gap-2 rounded-ele border border-ele-hairline p-2 text-sm text-ele-body max-sm:min-h-11">
                  <input type="radio" name="rebuild-mode" checked={mode === m} onChange={() => chooseMode(m)} disabled={busy !== null} className="mt-1 accent-ele-green" />
                  <span>
                    <span className="block text-ele-strong">{MODE_WORDS[m].title}</span>
                    <span className="block">{MODE_WORDS[m].blurb}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {preview && (
            <div role="status" className="mb-3 rounded-ele border border-ele-hairline bg-ele-surface p-3 text-sm text-ele-body" data-testid="rebuild-plan">
              <p className="break-words">{preview.message}</p>
              {!preview.nothingToDo && (
                <p className="mt-2 text-ele-strong">
                  It would act on {preview.confirmNumber.toLocaleString("en-GB")} {preview.confirmWhat}. Nothing has been started.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {note && (
        <p role="status" className="mb-3 text-sm text-ele-strong" data-testid="rebuild-note">
          {note}
        </p>
      )}
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className={`mt-2 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy !== null} className={MODAL_SECONDARY_BTN}>
          Close
        </button>
        {!live &&
          (preview && !preview.nothingToDo ? (
            <ConfirmButton
              onConfirm={() => void start()}
              label={`Start: rebuild ${preview.confirmNumber.toLocaleString("en-GB")} ${preview.confirmWhat}`}
              confirmLabel={`Press again: rebuild ${preview.confirmNumber.toLocaleString("en-GB")} ${preview.confirmWhat}`}
              busy={busy === "start"}
              busyLabel="Starting..."
              disabled={busy !== null}
              className={MODAL_PRIMARY_BTN}
            />
          ) : (
            <button type="button" onClick={() => void check()} disabled={busy !== null} className={MODAL_PRIMARY_BTN}>
              {busy === "preview" ? "Working it out..." : "Check what it would do"}
            </button>
          ))}
      </div>
    </ModalShell>
  );
}
